import { MoreSettings } from '@shared/assets/icons/components';
import { Flex, Popover } from 'antd';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@shared/ui/antd';

import styles from './channel-settings.module.scss';

interface ChannelSettingsProps {
  channelId: number;
  isActive: boolean;
  isToggleLoading: boolean;
  isHealthCheckLoading: boolean;
  onToggleActive: (status: boolean) => void;
  onHealthCheck: () => void;
  onSelectChannelToUpdate: (channelId: number) => void;
  onDeleteChannel: (channelId: number) => void;
  onCopyScript?: () => void;
}

export const ChannelSettings: FC<ChannelSettingsProps> = ({
  channelId,
  isActive,
  isToggleLoading,
  isHealthCheckLoading,
  onToggleActive,
  onHealthCheck,
  onSelectChannelToUpdate,
  onDeleteChannel,
  onCopyScript,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'channels.settings' });
  const [open, setOpen] = useState(false);

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
  };

  const handleClick = (callback: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    callback();
  };

  const items = [
    {
      key: 'update' as const,
      title: t('update'),
      onClick: onHealthCheck,
      disabled: isHealthCheckLoading,
    },
    {
      key: 'edit' as const,
      title: t('edit'),
      onClick: () => onSelectChannelToUpdate(channelId),
    },
    ...(onCopyScript
      ? [
          {
            key: 'copyScript' as const,
            title: t('copyScript'),
            onClick: onCopyScript,
          },
        ]
      : []),
    {
      key: 'toggle' as const,
      title: isActive ? t('deactivate') : t('activate'),
      onClick: () => onToggleActive(!isActive),
      disabled: isToggleLoading,
    },
    {
      key: 'delete' as const,
      title: t('delete'),
      onClick: () => onDeleteChannel(channelId),
    },
  ];

  const menu = (
    <Flex vertical>
      {items.map(
        (item) =>
          item.onClick && (
            <Flex
              key={item.key}
              align="center"
              justify="start"
              className={styles.root}
              onClick={item.disabled ? undefined : handleClick(item.onClick)}
              gap="8px"
              style={item.disabled ? { opacity: 0.5, cursor: 'default' } : undefined}
            >
              <Text variant="sm" fontWeight={600}>
                {item.title}
              </Text>
            </Flex>
          ),
      )}
    </Flex>
  );

  return (
    <Popover
      content={menu}
      trigger={'click'}
      open={open}
      onOpenChange={handleOpenChange}
      arrow={false}
    >
      <Flex
        justify="center"
        onClick={(e) => e.stopPropagation()}
        align="center"
        className={styles.container}
      >
        <MoreSettings />
      </Flex>
    </Popover>
  );
};
